import { useMutation, useQuery } from '@apollo/client';
import { withApollo } from '@apollo/client/react/hoc';
import gql from 'graphql-tag';
import { isEqual } from 'lodash';
import { lazy, Suspense, useEffect } from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { getCurrentUserFromCache, patchCurrentUser, refreshCurrentUser } from '../../common/currentUser';
import Body from '../../components/Layout/Body';
import Container from '../../components/Layout/Container';
import Footer from '../../components/Layout/Footer';
import Header from '../../components/Layout/Header';
import { LS_BADGE_CACHE, LS_USER_CACHE } from '../../config';
import { CurrentUserProvider } from '../../contexts/CurrentUserContext';
import { getContentLanguagesValues, getInterfaceLanguagesValues, useLanguage } from '../../contexts/LanguageContext';
import { useMeta } from '../../contexts/MetaContext';
import { useWindow } from '../../contexts/WindowContext';
import LanguagesSelectorWindow from '../Windows/LanguagesSelectorWindow';
import SignOutWindow from '../Windows/SignOutWindow';

import ChangeLanguageMutationGQL from './ChangeLanguageMutation.graphql';
import GetPinnedContestsQuery from './GetPinnedContestsQuery.graphql';
import SignOutMutationGQL from './SignOutMutation.graphql';

const Banner = lazy(() => import('./Banner'));
const LayoutAsync = lazy(() => import('./LayoutAsync'));

const arraySort = (x, y) => {
  if (x === y) return 0;
  else return (x > y) ? 1 : -1;
}

const Layout = ({ children, client }) => {
  const { data } = useQuery(gql(GetPinnedContestsQuery), {
    fetchPolicy: 'cache-first',
  });
  const { setNotificationCount } = useMeta();
  const [signOutMutation] = useMutation(gql(SignOutMutationGQL));
  const [changeLanguageMutation] = useMutation(gql(ChangeLanguageMutationGQL));
  const navigate = useNavigate();
  const { actions: windowActions } = useWindow();
  const [currentUser, setCurrentUser] = useState(getCurrentUserFromCache());
  const { currentContentLanguages, currentInterfaceLanguage, actions: languageActions } = useLanguage();

  const handleSignOut = async () => {
    windowActions.open({
      component: () => (
        <SignOutWindow
          onSignOut={async () => {
            windowActions.close();
            const signOutData = await signOutMutation();

            // If success, we purge the cache and redirect to the homepage
            if (signOutData?.data?.signOut === null) {
              localStorage.removeItem(LS_USER_CACHE);
              localStorage.removeItem(LS_BADGE_CACHE);
              navigate('/');
              client.resetStore();
            }
          }}
          onCancel={() => windowActions.close()}
        />
      ),
    });
  };

  const handleLanguagesChangeSubmit = async (data) => {
    windowActions.close();

    if (currentUser?.me?.id) {
      await changeLanguageMutation({
        variables: {
          input: {
            userId: currentUser?.me?.id,
            preferredLanguage: data.interfaceLanguage,
            contentLanguages: data.contentLanguages,
          },
        },
      }).catch(() => null);
      patchCurrentUser({
        preferredLanguage: data.interfaceLanguage,
        contentLanguages: data.contentLanguages,
      });
    } else {
      languageActions.setCurrentContentLanguages(data.contentLanguages);
      languageActions.setCurrentInterfaceLanguage(data.interfaceLanguage);
    }
  };

  const handleLanguagesChange = () => {
    windowActions.open({
      component: () => (
        <LanguagesSelectorWindow
          contentLanguages={getContentLanguagesValues()}
          interfaceLanguages={getInterfaceLanguagesValues()}
          currentContentLanguages={currentContentLanguages}
          currentInterfaceLanguage={currentInterfaceLanguage}
          onSubmit={handleLanguagesChangeSubmit}
        />
      ),
    });
  };

  useEffect(() => {
    refreshCurrentUser();
    const evt = setInterval(async () => {
      const computedData = await refreshCurrentUser();
      if (!isEqual(computedData, currentUser)) {
        setCurrentUser(computedData);
      }
      setNotificationCount(computedData?.unreadChats?.totalCount + computedData?.unreadNotifications?.totalCount);

      // Reapply language configuration if not sync
      if (currentUser?.me?.id) {
        if (!isEqual(currentContentLanguages.sort(arraySort), currentUser?.me?.contentLanguages?.sort(arraySort))) {
          languageActions.setCurrentContentLanguages(currentUser?.me?.contentLanguages);
        }
        if (currentInterfaceLanguage !== currentUser?.me?.preferredLanguage) {
          languageActions.setCurrentInterfaceLanguage(currentUser?.me?.preferredLanguage);
        }
      }
    }, 500);
    return () => clearInterval(evt);
  });

  const allowedActions = currentUser?.allowedActions || [];
  const contest = data?.contests?.edges?.[0]?.node;
  const contestsToJudge = data?.contestsToJudge?.edges?.map(({node}) => node);

  return (
    <Body>
      <CurrentUserProvider currentUser={currentUser?.me} allowedActions={allowedActions}>
        <Header contestsToJudge={contestsToJudge} contest={contest} user={currentUser?.me} allowedActions={allowedActions} unreadChats={currentUser?.unreadChats?.totalCount || 0} unreadNotifications={currentUser?.unreadNotifications?.totalCount || 0} onSignOut={handleSignOut} onLanguageClick={handleLanguagesChange} />
        <Container>
          <Suspense fallback={null}>
            <Banner currentUser={currentUser} />
          </Suspense>
          {children}
        </Container>
      </CurrentUserProvider>
      <Footer user={currentUser?.me} />
      <Suspense fallback={null}>
        <LayoutAsync currentUser={currentUser} />
      </Suspense>
    </Body>
  );
};

export default withApollo(Layout);
